// src/lib/utils/colors/core.ts

// Core color space type definitions

/**
 * Comprehensive color utilities and conversion library
 * Provides type-safe color conversions between multiple color spaces
 * with full TypeScript support and JSDoc documentation
 */

import type { RGB, HSV, HWB, CMYK, XYZ, LAB, OKLAB, LCH, OKLCH, HCG, Apple, Gray, HEX, Keyword, ANSI16, ANSI256, ColorSpace, ColorValue } from '~/lib/utils/colors/types'
import { rgbToHsl, rgbToHsv, rgbToHwb, rgbToCmyk, rgbToXyz, rgbToLab, rgbToOklab, labToLch, oklabToOklch, rgbToHcg, rgbToApple, rgbToGrayscale, rgbToHex, rgbToKeyword, rgbToAnsi16, rgbToAnsi256, parseColorString, convertColor, formatRgbString, formatHslString, lighten, darken, saturate, desaturate, rotateHue, mixColors, clamp } from '~/lib/utils/colors/transforms'
import { getLuminance, getContrastRatio, isDark, isLight } from '~/lib/utils/colors/color-relationships'

/**
 * Color class for comprehensive color manipulation and conversion
 */
class ColorClass {
  private _rgb: RGB
  private _alpha: number

  /**
   * Creates a new Color instance
   * @param {ColorValue | string} input - Color input value or string
   * @param {ColorSpace} space - Color space of input (optional for strings)
   * @param {number} alpha - Alpha value [0-1]
   */
  constructor(input: ColorValue | string, space?: ColorSpace, alpha = 1) {
    this._alpha = clamp(alpha, 0, 1)

    if (typeof input === 'string') {
      const parsed = parseColorString(input)
      if (!parsed) throw new Error(`Invalid color string: ${input}`)
      this._rgb = parsed
    } else if (space) {
      this._rgb = convertColor(input, space, 'rgb') as RGB
    } else {
      // Assume RGB if no space specified
      this._rgb = input as RGB
    }
  }

  /**
   * Gets hue value
   * @returns {number} Hue value [0-360]
   */
  hue(): number {
    return rgbToHsl(this._rgb)[0]
  }

  /**
   * Gets saturation value
   * @returns {number} Saturation value [0-100]
   */
  saturationl(): number {
    return rgbToHsl(this._rgb)[1]
  }

  /**
   * Gets lightness value
   * @returns {number} Lightness value [0-100]
   */
  lightness(): number {
    return rgbToHsl(this._rgb)[2]
  }

  /**
   * Gets alpha value
   * @returns {number} Alpha value [0-1]
   */
  alpha(): number {
    return this._alpha
  }

  /**
   * Gets RGB values as object with array method
   * @returns {Object} RGB object with array method
   */
  rgb(): { array: () => [number, number, number] } {
    return {
      array: () => [this._rgb[0], this._rgb[1], this._rgb[2]]
    }
  }

  /**
   * Gets HSL values as object with array method
   * @returns {Object} HSL object with array method
   */
  hsl(): { array: () => [number, number, number] } {
    const hsl = rgbToHsl(this._rgb)
    return {
      array: () => [hsl[0], hsl[1], hsl[2]]
    }
  }

  /**
   * Gets HSV values
   * @returns {HSV} HSV color values
   */
  get hsv(): HSV {
    return rgbToHsv(this._rgb)
  }

  /**
   * Gets HWB values
   * @returns {HWB} HWB color values
   */
  get hwb(): HWB {
    return rgbToHwb(this._rgb)
  }

  /**
   * Gets CMYK values
   * @returns {CMYK} CMYK color values
   */
  get cmyk(): CMYK {
    return rgbToCmyk(this._rgb)
  }

  /**
   * Gets XYZ values
   * @returns {XYZ} XYZ color values
   */
  get xyz(): XYZ {
    return rgbToXyz(this._rgb)
  }

  /**
   * Gets LAB values
   * @returns {LAB} LAB color values
   */
  get lab(): LAB {
    return rgbToLab(this._rgb)
  }

  /**
   * Gets OKLAB values
   * @returns {OKLAB} OKLAB color values
   */
  get oklab(): OKLAB {
    return rgbToOklab(this._rgb)
  }

  /**
   * Gets LCH values
   * @returns {LCH} LCH color values
   */
  get lch(): LCH {
    return labToLch(this.lab)
  }

  /**
   * Gets OKLCH values
   * @returns {OKLCH} OKLCH color values
   */
  get oklch(): OKLCH {
    return oklabToOklch(this.oklab)
  }

  /**
   * Gets HCG values
   * @returns {HCG} HCG color values
   */
  get hcg(): HCG {
    return rgbToHcg(this._rgb)
  }

  /**
   * Gets Apple RGB values
   * @returns {Apple} Apple RGB color values
   */
  get apple(): Apple {
    return rgbToApple(this._rgb)
  }

  /**
   * Gets grayscale value
   * @returns {Gray} Grayscale color value
   */
  get gray(): Gray {
    return rgbToGrayscale(this._rgb)
  }

  /**
   * Gets HEX string
   * @returns {HEX} HEX color string
   */
  hex(): HEX {
    return rgbToHex(this._rgb)
  }

  /**
   * Gets keyword name
   * @returns {Keyword} CSS keyword name
   */
  get keyword(): Keyword {
    return rgbToKeyword(this._rgb)
  }

  /**
   * Gets ANSI 16-color code
   * @returns {ANSI16} ANSI 16-color code
   */
  get ansi16(): ANSI16 {
    return rgbToAnsi16(this._rgb)
  }

  /**
   * Gets ANSI 256-color code
   * @returns {ANSI256} ANSI 256-color code
   */
  get ansi256(): ANSI256 {
    return rgbToAnsi256(this._rgb)
  }


  /**
   * Sets alpha value and returns new Color instance
   * @param {number} alpha - Alpha value [0-1]
   * @returns {Color} New Color instance with updated alpha
   */
  setAlpha(alpha: number): ColorClass {
    return new ColorClass(this._rgb, 'rgb', alpha)
  }

  /**
   * Lightens the color by a given ratio
   * @param {number} ratio - Lightening ratio [0-1]
   * @returns {Color} New lightened Color instance
   */
  lighten(ratio: number): ColorClass {
    const hsl = lighten(rgbToHsl(this._rgb), ratio)
    return new ColorClass(hsl, 'hsl', this._alpha)
  }

  /**
   * Darkens the color by a given ratio
   * @param {number} ratio - Darkening ratio [0-1]
   * @returns {Color} New darkened Color instance
   */
  darken(ratio: number): ColorClass {
    const hsl = darken(rgbToHsl(this._rgb), ratio)
    return new ColorClass(hsl, 'hsl', this._alpha)
  }

  /**
   * Saturates the color by a given ratio
   * @param {number} ratio - Saturation ratio [0-1]
   * @returns {Color} New saturated Color instance
   */
  saturate(ratio: number): ColorClass {
    const hsl = saturate(rgbToHsl(this._rgb), ratio)
    return new ColorClass(hsl, 'hsl', this._alpha)
  }

  /**
   * Desaturates the color by a given ratio
   * @param {number} ratio - Desaturation ratio [0-1]
   * @returns {Color} New desaturated Color instance
   */
  desaturate(ratio: number): ColorClass {
    const hsl = desaturate(rgbToHsl(this._rgb), ratio)
    return new ColorClass(hsl, 'hsl', this._alpha)
  }

  /**
   * Rotates the hue by degrees
   * @param {number} degrees - Degrees to rotate hue
   * @returns {Color} New Color instance with rotated hue
   */
  rotate(degrees: number): ColorClass {
    const hsl = rotateHue(rgbToHsl(this._rgb), degrees)
    return new ColorClass(hsl, 'hsl', this._alpha)
  }

  /**
   * Mixes this color with another color
   * @param {Color} other - Other color to mix with
   * @param {number} weight - Mix weight [0-1]
   * @returns {Color} New mixed Color instance
   */
  mix(other: ColorClass, weight = 0.5): ColorClass {
    const mixed = mixColors(this._rgb, other._rgb, weight)
    const mixedAlpha = this._alpha * (1 - weight) + other._alpha * weight
    return new ColorClass(mixed, 'rgb', mixedAlpha)
  }

  /**
   * Gets the luminance of the color
   * @returns {number} Luminance value [0-1]
   */
  luminance(): number {
    return getLuminance(this._rgb)
  }

  /**
   * Calculates contrast ratio with another color
   * @param {Color} other - Other color to compare with
   * @returns {number} Contrast ratio [1-21]
   */
  contrast(other: ColorClass): number {
    return getContrastRatio(this._rgb, other._rgb)
  }

  /**
   * Gets WCAG accessibility level with another color
   * @param {Color} other - Other color to compare with
   * @returns {string} WCAG level ('AAA', 'AA', or '')
   */
  level(other: ColorClass): string {
    const contrastRatio = this.contrast(other)
    if (contrastRatio >= 7) {
      return 'AAA'
    }
    return (contrastRatio >= 4.5) ? 'AA' : ''
  }

  /**
   * Determines if the color is dark
   * @returns {boolean} True if the color is dark
   */
  isDark(): boolean {
    return isDark(this._rgb)
  }

  /**
   * Determines if the color is light
   * @returns {boolean} True if the color is light
   */
  isLight(): boolean {
    return isLight(this._rgb)
  }

  /**
   * Converts to CSS string representation
   * @param {'rgb' | 'hsl' | 'hex'} format - Output format
   * @returns {string} CSS color string
   */
  toString(format: 'rgb' | 'hsl' | 'hex' = 'hex'): string {
    switch (format) {
      case 'rgb':
        return formatRgbString(this._rgb, this._alpha < 1 ? this._alpha : undefined)
      case 'hsl':
        return formatHslString(rgbToHsl(this._rgb), this._alpha < 1 ? this._alpha : undefined)
      case 'hex':
        return this.hex()
      default:
        return this.hex()
    }
  }

  /**
   * Creates a Color instance from RGB values
   * @param {number} r - Red component [0-255]
   * @param {number} g - Green component [0-255]
   * @param {number} b - Blue component [0-255]
   * @param {number} alpha - Alpha value [0-1]
   * @returns {Color} New Color instance
   */
  static fromRgb(r: number, g: number, b: number, alpha = 1): ColorClass {
    return new ColorClass([r, g, b], 'rgb', alpha)
  }

  /**
   * Creates a Color instance from RGB values
   * @param {number} r - Red component [0-255]
   * @param {number} g - Green component [0-255]
   * @param {number} b - Blue component [0-255]
   * @param {number} alpha - Alpha value [0-1]
   * @returns {Color} New Color instance
   */
  static rgb(r: number, g: number, b: number, alpha = 1): ColorClass {
    return new ColorClass([r, g, b], 'rgb', alpha)
  }

  /**
   * Creates a Color instance from HSL values
   * @param {number} h - Hue [0-360]
   * @param {number} s - Saturation [0-100]
   * @param {number} l - Lightness [0-100]
   * @param {number} alpha - Alpha value [0-1]
   * @returns {Color} New Color instance
   */
  static fromHsl(h: number, s: number, l: number, alpha = 1): ColorClass {
    return new ColorClass([h, s, l], 'hsl', alpha)
  }

  /**
   * Creates a Color instance from HSL values
   * @param {number} h - Hue [0-360]
   * @param {number} s - Saturation [0-100]
   * @param {number} l - Lightness [0-100]
   * @param {number} alpha - Alpha value [0-1]
   * @returns {Color} New Color instance
   */
  static hsl(h: number, s: number, l: number, alpha = 1): ColorClass {
    return new ColorClass([h, s, l], 'hsl', alpha)
  }

  /**
   * Creates a Color instance from HEX string
   * @param {string} hex - HEX color string
   * @param {number} alpha - Alpha value [0-1]
   * @returns {Color} New Color instance
   */
  static fromHex(hex: string, alpha = 1): ColorClass {
    return new ColorClass(hex, undefined, alpha)
  }

  /**
   * Creates a Color instance from keyword
   * @param {string} keyword - CSS keyword name
   * @param {number} alpha - Alpha value [0-1]
   * @returns {Color} New Color instance
   */
  static fromKeyword(keyword: string, alpha = 1): ColorClass {
    return new ColorClass(keyword, undefined, alpha)
  }
}

/**
 * Factory function for creating Color instances
 * This allows calling Color() without 'new' for backward compatibility
 * 
 * @param {ColorValue | string} input - Color input value or string
 * @param {ColorSpace} space - Color space of input (optional for strings)
 * @param {number} alpha - Alpha value [0-1]
 * @returns {Color} New Color instance
 */
function createColor(input: ColorValue | string, space?: ColorSpace, alpha = 1): ColorClass {
  return new ColorClass(input, space, alpha)
}

// Export the factory function as the default Color function
export { createColor as Color, ColorClass }

